import { useRef, useState, useEffect } from 'react';

const SignatureModalForMobile = ({ isOpen, onClose, onSaveSignature }) => {
  const containerRef = useRef(null);
  const [paths, setPaths] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const bounds = containerRef.current.getBoundingClientRect();
        const width = bounds.width;
        const height = bounds.height;
        setCanvasSize({ width, height });
      }
    };
    updateSize();
  }, [isPortrait]);

  const getOffset = (inputEvent) => {
    const bounds = containerRef.current.getBoundingClientRect();
    const x = inputEvent.clientX - bounds.left;
    const y = inputEvent.clientY - bounds.top;
    return { offsetX: x, offsetY: y };
  };

  const pathToD = (path) =>
    path.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]},${p[1]}`).join(' ');

  const startDrawing = (e) => {
    if (isPortrait) return; 
    const { offsetX, offsetY } = getOffset(e);
    setCurrentPath([[offsetX, offsetY]]);
    setIsDrawing(true);
  };

 

  const stopDrawing = () => {
    if (currentPath.length > 0) {
      setPaths((prev) => [...prev, currentPath]);
      setCurrentPath([]);
    }
    setIsDrawing(false);
  };

const draw = (e) => {
  if (!isDrawing || isPortrait) return;
  const { offsetX, offsetY } = getOffset(e);
  setCurrentPath((prev) => [...prev, [offsetX, offsetY]]);
};

const startTouch = (e) => {
  if (isPortrait) return; // ✨ Block touch signing in portrait
  e.preventDefault();
  const { offsetX, offsetY } = getOffset(e.touches[0]);
  setCurrentPath([[offsetX, offsetY]]);
  setIsDrawing(true);
};

const drawTouch = (e) => {
  if (!isDrawing || isPortrait) return; // ✨ Block touch drawing
  e.preventDefault();
  const { offsetX, offsetY } = getOffset(e.touches[0]);
  setCurrentPath((prev) => [...prev, [offsetX, offsetY]]);
};

  const stopTouch = (e) => {
    e.preventDefault();
    if (currentPath.length > 0) {
      setPaths((prev) => [...prev, currentPath]);
      setCurrentPath([]);
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    setPaths([]);
    setCurrentPath([]);
  };

  const saveSignature = () => {
    const svgDataUrl = generateSVGDataURL(paths, canvasSize);
    onSaveSignature(svgDataUrl);
    onClose();
  };


  return (
    <>
      {/* Embedded CSS styles */}
      <style>{`
        @keyframes tilt {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(15deg); }
        }

        .tilt-animation {
          animation: tilt 1.5s infinite;
        }

        .rotate-warning {
          position: fixed;
          top: 10%;
          left: 50%;
          transform: translateX(-50%);
          background-color: #ffc107;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: bold;
          color: #000;
          z-index: 9999;
        }
      `}</style>

      {/* Rotate prompt */}
      {isPortrait && (
        <div className="rotate-warning tilt-animation">
          Please rotate your device to sign.
        </div>
      )}

      {/* Signature canvas */}
      <div className="row">
        <div
          className="col-md-12 d-flex justify-content-center align-items-center"
          style={{
            width: isPortrait ? window.innerHeight + 'px' : '100%',
            height: isPortrait ? window.innerWidth + 'px' : '50vh',
            transform: isPortrait ? 'rotate(90deg)' : 'none',
            transformOrigin: 'center center',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1
          }}
          ref={containerRef}
        >
          <svg
            width={canvasSize.width}
            height={canvasSize.height}
            className="border border-label-primary bg-label-dark rounded border border-dashed border-5"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startTouch}
            onTouchMove={drawTouch}
            onTouchEnd={stopTouch}
          >
            {paths.map((path, index) => (
              <path key={index} d={pathToD(path)} stroke="black" strokeWidth="3" fill="none" />
            ))}
            {currentPath.length > 0 && (
              <path d={pathToD(currentPath)} stroke="black" strokeWidth="3" fill="none" />
            )}
            {paths.length === 0 && currentPath.length === 0 && (
              <text
                x="50%"
                y="50%"
                fill="gray"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="24"
              >
                Sign here.
              </text>
            )}
          </svg>
        </div>
      </div>

      {/* Disclaimer text */}
      <div className={`row mt-0 ${isPortrait ?'ms-4':''}`}>
        <span
          className="mb-0 fw-bold text-center"
          style={{
            display: 'block',
            transform: isPortrait ? 'rotate(90deg)' : 'none',
            transformOrigin: 'center center',
            width: isPortrait ? '100vh' : '100%',
            margin: isPortrait ? '6rem 0' : '0',
          }}
        >
          By signing this application with an electronic signature, you agree that such signature will
          be as valid as handwritten signatures to the extent allowed by local law.
        </span>

        {/* Buttons */}
        {!isPortrait && (
          <div className="col-12 d-flex justify-content-between px-3 mt-0">
            <button type="button" className="btn btn-label-secondary" onClick={clearSignature}>
              <i className="bx bx-trash bx-sm ms-sm-n2" />
              <span className="align-middle d-sm-inline-block d-none">Clear Signature</span>
            </button>
            <button type="button" className="btn btn-primary" onClick={saveSignature}>
              <span className="align-middle d-sm-inline-block d-none me-sm-1">Save Signature</span>
              <i className="bx bx-save bx-sm me-sm-n2" />
            </button>
          </div>
        )}

      </div>
    </>
  );
};

// Export to SVG data URL
const generateSVGDataURL = (paths, size) => {
  const pathD = paths
    .map((path) => path.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]},${p[1]}`).join(' '))
    .map((d) => `<path d="${d}" stroke="black" stroke-width="3" fill="none"/>`)
    .join('');

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size.width}" height="${size.height}">
      ${pathD}
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

export default SignatureModalForMobile;
